import { Theme } from '@eversports/react-components/design-tokens/theme'
import NotFound from '@eversports/react-components/patterns/NotFound'
import Flex from '@eversports/react-components/primitives/Flex'
import { makeStyles } from '@mui/styles'
import React from 'react'
import { Route, Switch } from 'react-router'
import { useLocation } from 'react-router-dom'
import { AnimatePresence } from '@eversports/react-components/design-tokens/motion'
import useTheme from '@eversports/react-components/design-tokens/useTheme'
import useMediaQuery from '@eversports/react-components/design-tokens/useMediaQuery'

import { DesktopBookingDetails } from '../components/BookingDetails'
import FamilyAccountBanner from '../components/FamilyAccountBanner'
import Header from '../components/Header'
import { HEADER_HEIGHT } from '../components/Header/HeaderComponent'
import PaymentTimer from '../components/PaymentTimer'
import Stepper from '../components/Stepper'
import { DesktopSummary, MobileSummary } from '../components/Summary'
import Routes from '../data/routes'
import AdditionalInformation from '../screens/AdditionalInformation'
import ChooseProduct from '../screens/ChooseProduct'
import Confirmation from '../screens/Confirmation'
import Payment from '../screens/Payment'
import ReviewInformation from '../screens/ReviewInformation'

import Page from './Page'

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    [theme.breakpoints.down('md')]: {
      // Size of the closed mobile summary
      // We need this so the body is still fully scrollable with the fixed summary
      marginBottom: 122,
    },
  },
  section: {
    padding: theme.layoutSpacing.default,
    [theme.breakpoints.down('md')]: {
      padding: 0,
    },
  },
  aside: {
    maxWidth: 350,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  fullHeight: {
    height: `calc(100% - ${HEADER_HEIGHT}px)`,
  },
  hiddenInMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  visibleInMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
}))

const Layout = () => {
  const classes = useStyles()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const isConfirmationPage = location.pathname === Routes.CONFIRMATION
  const isUnknownRoute = !Object.values(Routes).includes(location.pathname as Routes)

  return (
    <>
      <Header />
      {!isConfirmationPage && <FamilyAccountBanner />}
      {isUnknownRoute && (
        <Flex alignItems="center" justifyContent="center" className={classes.fullHeight} fullWidth>
          <NotFound />
        </Flex>
      )}
      {isConfirmationPage && <Confirmation />}
      {!isUnknownRoute && !isConfirmationPage && (
        <Page>
          <Stepper />
          <Flex flexDirection={['column', 'row']}>
            <Flex
              is="main"
              flexGrow={1}
              flexDirection="column"
              className={classes.main}
              verticalSpacing="wide"
              marginRight={['none', 'none', 'hero']}
            >
              <div className={classes.hiddenInMobile}>
                <DesktopBookingDetails />
              </div>
              <div className={classes.section}>
                <AnimatePresence exitBeforeEnter>
                  <Switch location={location} key={location.key}>
                    <Route path={Routes.CHOOSE_PRODUCT}>
                      <ChooseProduct />
                    </Route>
                    <Route path={Routes.REVIEW_INFORMATION}>
                      <ReviewInformation />
                    </Route>
                    <Route path={Routes.ADDITIONAL_INFORMATION}>
                      <AdditionalInformation />
                    </Route>
                    <Route path={Routes.PAYMENT}>
                      <PaymentTimer />
                      <Payment />
                    </Route>
                  </Switch>
                </AnimatePresence>
              </div>
            </Flex>
            <Flex is="aside" className={classes.aside} marginLeft="default" fullWidth flexShrink={0}>
              <DesktopSummary />
            </Flex>
            {isMobile && (
              <div className={classes.visibleInMobile}>
                <MobileSummary />
              </div>
            )}
          </Flex>
        </Page>
      )}
    </>
  )
}

export default Layout
